import {
  CustomFieldDisplay,
  CustomFieldType,
  OrganizationCustomFieldConfig
} from 'generated-graphql/hooks/apollo-hooks';
import {
  buildCandidateFilterTree,
  isFreeTextField
} from 'modules/candidate-profile/tables/filters/buildCandidateFilterTree';
import { UNIVERSAL_FILTER_FIELDS } from 'modules/candidate-profile/tables/filters/universalFilterFields';

export { UNIVERSAL_FILTER_FIELDS } from 'modules/candidate-profile/tables/filters/universalFilterFields';
import {
  CandidateFilterGroups,
  CandidateFilterState,
  CandidateFilterValueType,
  SourcingSearchRequest,
  SourcingFilterField,
  RangeFilterSettings
} from 'modules/candidate-profile/types/CandidateSearchFilter';

const CUSTOM_FIELD_TYPES: Record<CustomFieldType, CandidateFilterValueType> = {
  [CustomFieldType.Text]: CandidateFilterValueType.TEXT,
  [CustomFieldType.TextArray]: CandidateFilterValueType.TEXT_ARRAY,
  [CustomFieldType.Number]: CandidateFilterValueType.NUMBER,
  [CustomFieldType.Date]: CandidateFilterValueType.DATE,
  [CustomFieldType.Boolean]: CandidateFilterValueType.BOOLEAN
};

export const rangeFilterSettingsForCustomField = ({
  type,
  displayAs
}: OrganizationCustomFieldConfig): RangeFilterSettings | undefined => {
  if (type === CustomFieldType.Number) return { kind: 'number' };
  if (type === CustomFieldType.Date && displayAs === CustomFieldDisplay.Age) {
    return { kind: 'number', unit: 'years', showsAge: true };
  }
  if (type === CustomFieldType.Date) return { kind: 'date' };
  return undefined;
};

/**
 * Required by the endpoint: query expansion only generates filters over the
 * fields it is given. Retired and unmapped fields are dropped — the endpoint
 * 400s on any entry it cannot resolve.
 */
export const buildAvailableFilters = (
  customFieldConfigs: OrganizationCustomFieldConfig[]
): SourcingFilterField[] => [
  ...UNIVERSAL_FILTER_FIELDS,
  ...customFieldConfigs
    .filter((config) => !config.isDeleted && Boolean(config.vectorDbKey))
    .map((config) => {
      const range = rangeFilterSettingsForCustomField(config);
      return {
        field: config.fieldKey,
        type: CUSTOM_FIELD_TYPES[config.type],
        label: config.label,
        vectorDbKey: config.vectorDbKey,
        ...(config.allowedValues?.length
          ? { allowedValues: config.allowedValues }
          : {}),
        ...(range ? { range } : {})
      };
    })
];

const toContractField = ({
  field,
  type,
  label,
  vectorDbKey,
  allowedValues
}: SourcingFilterField): SourcingFilterField => ({
  field,
  type,
  ...(label ? { label } : {}),
  ...(vectorDbKey ? { vectorDbKey } : {}),
  ...(allowedValues?.length ? { allowedValues } : {})
});

type BuildSourcingSearchRequestParams = {
  organizationId?: string;
  userId?: string;
  filters: CandidateFilterState[];
  groups?: CandidateFilterGroups;
  customFieldConfigs: OrganizationCustomFieldConfig[];
  searchId?: string;
  messageBody?: string;
  includeExplanations?: boolean;
  likeCandidateNames?: string[];
  skipReply?: boolean;
};

export const buildSourcingSearchRequest = ({
  organizationId,
  userId,
  filters,
  groups,
  customFieldConfigs,
  searchId,
  messageBody,
  // Explanations and the reply are separate calls; both default true server-side.
  includeExplanations = false,
  likeCandidateNames,
  skipReply = true
}: BuildSourcingSearchRequestParams): SourcingSearchRequest => {
  const availableFilters = buildAvailableFilters(customFieldConfigs);
  const filter = buildCandidateFilterTree(filters, groups, (field) =>
    isFreeTextField(field, availableFilters)
  );

  return {
    ...(organizationId ? { organizationId } : {}),
    ...(userId ? { userId } : {}),
    availableFilters: availableFilters.map(toContractField),
    ...(searchId ? { id: searchId } : {}),
    ...(messageBody ? { messageBody } : {}),
    ...(filter ? { filter } : {}),
    includeExplanations,
    ...(likeCandidateNames?.length ? { likeCandidateNames } : {}),
    skipReply
  };
};
